/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import Dashboard from "../../../services/api/dashboard";

// ── Keyframes ─────────────────────────────────────────
const fillBar = (pct) => keyframes`
  from { width: 0%; }
  to   { width: ${pct}%; }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0%   { opacity: 0; transform: scale(0.72) translateY(6px); }
  70%  { transform: scale(1.07) translateY(-2px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Root ──────────────────────────────────────────────
const Root = styled(Box)(({ theme }) => ({
  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
  background: "#ffffff",
  borderRadius: "14px",
  overflow: "hidden",
  boxShadow: "0 10px 48px rgba(0,0,0,0.26), 0 3px 10px rgba(0,0,0,0.14)",
  animation: `${fadeUp} 0.45s ease both`,
  flex: 1,
  minWidth: "300px",
  [theme.breakpoints.down("sm")]: {
    minWidth: "100%",
    flex: "unset",
  },
}));

// ── Gradient Banner ────────────────────────────────────
const GradientBanner = styled(Box)(({ gradient, theme }) => ({
  background: gradient || "linear-gradient(135deg, #7c3aed, #1d4ed8)",
  padding: "10px 14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "8px",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-60%",
    right: "8%",
    width: "38%",
    height: "260%",
    background: "rgba(255,255,255,0.09)",
    transform: "rotate(18deg)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "rgba(255,255,255,0.18)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: "10px 12px",
    gap: "6px",
  },
}));

const BannerLeft = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  zIndex: 1,
  [theme.breakpoints.down("sm")]: {
    maxWidth: "55%",
  },
}));

const BannerTitle = styled(Box)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 700,
  color: "#ffffff",
  letterSpacing: "0.01em",
  lineHeight: 1.2,
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
  },
}));

// ── Banner Right — Achieved label + % + progress bar ──
const BannerPercent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  zIndex: 1,
  width: "50%",

  [theme.breakpoints.down("sm")]: {
    width: "auto", // ✅ 50% hata do
    alignItems: "flex-start", // same left jaisa flow maintain
  },
}));

const PercentLabel = styled(Box)(() => ({
  fontSize: "10px",
  fontWeight: 700,
  color: "rgba(255,255,255,0.70)",
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  textAlign: "right",
}));

const PercentBig = styled(Box)(({ theme }) => ({
  fontSize: "30px",
  fontWeight: 800,
  color: "#ffffff",
  lineHeight: 1,
  letterSpacing: "-0.04em",
  textShadow: "0 2px 16px rgba(0,0,0,0.18)",
  animation: `${slideIn} 0.6s 0.2s ease both`,
  [theme.breakpoints.down("sm")]: {
    fontSize: "24px",
  },
}));

// ── Banner Progress Bar — banner ke andar ─────────────
const BannerTrack = styled(Box)(() => ({
  width: "100%", // ← BannerPercent ki full width (= card ka 50%)
  height: "5px",
  background: "rgba(255,255,255,0.22)",
  borderRadius: "999px",
  overflow: "hidden",
  marginTop: "6px",
}));

const BannerFill = styled(Box)(({ pct, fillcolor, bardelay }) => ({
  height: "100%",
  width: `${pct}%`,
  borderRadius: "999px",
  background: fillcolor || "rgba(255,255,255,0.85)",
  boxShadow: "0 0 6px rgba(255,255,255,0.40)",
  animation: `${fillBar(pct)} 1.1s ${
    bardelay || "0s"
  } cubic-bezier(.4,0,.2,1) both`,
}));

const Badge = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  background: "rgba(255,255,255,0.18)",
  backdropFilter: "blur(6px)",
  color: "#fff",
  fontWeight: 600,
  fontSize: "11px",
  letterSpacing: "0.03em",
  padding: "3px 9px",
  borderRadius: "6px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    padding: "2px 7px",
  },
}));

const ReportButton = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(255,255,255,0.14)",
  backdropFilter: "blur(6px)",
  color: "#fff",
  fontWeight: 600,
  fontSize: "11px",
  letterSpacing: "0.02em",
  padding: "3px 9px",
  borderRadius: "999px",
  cursor: "pointer",
  userSelect: "none",
  border: "1px solid rgba(255,255,255,0.28)",
  transition: "all 0.2s ease",
  ":hover": {
    background: "rgba(255,255,255,0.28)",
    transform: "translateY(-1px)",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    padding: "2px 7px",
  },
}));

// ── Card Body ──────────────────────────────────────────
const CardBody = styled(Box)(({ theme }) => ({
  padding: "10px 14px 10px",
  [theme.breakpoints.down("sm")]: {
    padding: "10px 10px 10px",
  },
}));

// ── Stats Row ──────────────────────────────────────────
const StatsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "stretch",
  marginBottom: "8px",
  background: "#f8f7ff",
  borderRadius: "10px",
  overflow: "hidden",
  border: "1px solid #ede9fe",
  [theme.breakpoints.down("xs")]: {
    flexDirection: "column",
  },
}));

const StatBlock = styled(Box)(({ accent }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding: "6px 10px",
  borderLeft: `3px solid ${accent || "#7c3aed"}`,
}));

const StatDivider = styled(Box)(() => ({
  width: "1px",
  background: "#ede9fe",
  margin: "6px 0",
  flexShrink: 0,
}));

const StatLabel = styled(Box)(() => ({
  fontSize: "10px",
  fontWeight: 600,
  color: "#94a3b8",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  marginBottom: "3px",
}));

const StatValue = styled(Box)(({ accent, theme }) => ({
  fontSize: "17px",
  fontWeight: 700,
  color: accent || "#1e1b4b",
  lineHeight: 1.1,
  letterSpacing: "-0.02em",
  animation: `${slideIn} 0.5s 0.3s ease both`,
  [theme.breakpoints.down("sm")]: {
    fontSize: "15px",
  },
}));

// ── Category Grid ──────────────────────────────────────
const CategoryGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(6, 1fr)",
  gap: "4px",
  marginBottom: "8px",
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
}));

const CategoryBox = styled(Box)(({ accent, boxdelay }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "3px",
  background: `${accent}0d`,
  border: `1.5px solid ${accent}28`,
  borderTop: `3px solid ${accent}`,
  borderRadius: "7px",
  padding: "6px 2px 5px",
  animation: `${popIn} 0.38s ${
    boxdelay || "0s"
  } cubic-bezier(.34,1.56,.64,1) both`,
  transition: "all 0.22s ease",
  "&:hover": {
    background: `${accent}1a`,
    transform: "translateY(-2px)",
    boxShadow: `0 4px 14px ${accent}30`,
  },
}));

const CatValue = styled(Box)(({ accent }) => ({
  fontSize: "13px",
  fontWeight: 800,
  color: accent || "#1e1b4b",
  lineHeight: 1,
}));

const CatLabel = styled(Box)(() => ({
  fontSize: "9px",
  fontWeight: 700,
  color: "#94a3b8",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  lineHeight: 1,
}));

// ── Footer ─────────────────────────────────────────────
const Footer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "5px",
}));

const FootNote = styled(Box)(() => ({
  fontSize: "10px",
  color: "#94a3b8",
  fontWeight: 500,
}));

const Remaining = styled(Box)(({ remcolor }) => ({
  fontSize: "11px",
  fontWeight: 700,
  color: remcolor || "#f59e0b",
  background: `${remcolor || "#f59e0b"}14`,
  border: `1px solid ${remcolor || "#f59e0b"}35`,
  borderRadius: "5px",
  padding: "2px 7px",
}));

// ── Container ──────────────────────────────────────────
const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "stretch",
  flexWrap: "wrap",
  gap: "12px",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "12px",
  },
}));

// ── Component ──────────────────────────────────────────
function BsgVision() {
  const navigate = useNavigate();
  const [BSGData, setBSGData] = useState(null);
  const [unitData, setUnitData] = useState(null);
  const [dashboardSettings, setDashboardSettings] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await Dashboard.BsgVisionDataForUnit();
        console.log("Response from API:", response);
        const apiData = response?.data;
        if (!apiData) throw new Error("No data returned from API");
        setUnitData(apiData);
      } catch (err) {
        console.error("Error fetching Bsg Vision:", err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await Dashboard.dashboardSetting();
        console.log("Dashboard Setting API:", response);
        setDashboardSettings(response?.data || []);
      } catch (err) {
        console.error("Failed to fetch dashboard settings", err);
      }
    };
    fetchSettings();
  }, []);

  const areaId = localStorage.getItem("areaId");

  let areaMultiplier = 1;
  if (dashboardSettings?.length) {
    const nationalSetting = dashboardSettings.find(
      (s) => s.key === "chairman_national",
    );
    if (nationalSetting) areaMultiplier = Number(nationalSetting.value) || 1;
    const areaSetting = dashboardSettings.find((s) => s.key === areaId);
    if (areaSetting) areaMultiplier = Number(areaSetting.value) || 1;
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response1 = await Dashboard.BsgVisionData();
        const response2 = await Dashboard.BsgVisionDataForUnit();
        const bsg = response1?.data;
        const unit = response2?.data;
        setBSGData(Array.isArray(bsg) ? bsg[0] : bsg);
        setUnitData(Array.isArray(unit) ? unit[0] : unit);
      } catch (err) {
        console.error("Error fetching Bsg Vision:", err);
      }
    };
    fetchData();
  }, []);

  // ── Alag se function ──────────────────────────────
  // const getChairmanNationalValue = () => {
  //   if (!dashboardSettings?.length) return 1;

  //   const nationalSetting = dashboardSettings.find(
  //     (s) => s.key === "chairman_national"
  //   );

  //   return Number(nationalSetting?.value) || 1;
  // };

  // ── Phir totalGoal calculate karo
  // const chairmanNationalValue = getChairmanNationalValue();
  // const totalGoal = (BSGData?.districtCount || 0) * chairmanNationalValue;
  const totalGoal = BSGData?.total_target ?? 0;
  const achieved = BSGData?.memberCount ?? 0;
  const percent = totalGoal ? +((achieved / totalGoal) * 100).toFixed(1) : 0;

  const delhiGoal = (unitData?.districtCount || 0) * areaMultiplier;
  const delhiAchieved = unitData?.memberCount || 0;
  const delhiPercent = delhiGoal
    ? +((delhiAchieved / delhiGoal) * 100).toFixed(1)
    : 0;

  const UnitName = localStorage.getItem("UnitName");
  const positionRole = localStorage.getItem("positionRole");
  const NATIONAL_ROLES = [
  "national",
  "chairperson",
  "dy.",
];

const isNationalRole = NATIONAL_ROLES.includes(
  positionRole?.toLowerCase()?.trim()
);

  const UnitCategories = unitData
    ? [
        { label: "MD", value: unitData.md, accent: "#2563eb" },
        { label: "WD", value: unitData.wd, accent: "#7c3aed" },
        { label: "YMD", value: unitData.ymd, accent: "#0891b2" },
        { label: "YWD", value: unitData.ywd, accent: "#059669" },
        { label: "FD(B)", value: unitData.fdB, accent: "#dc2626" },
        { label: "FD(G)", value: unitData.fdG, accent: "#d97706" },
      ]
    : [];

  return (
    <Container>
      <Root>
        <GradientBanner gradient="linear-gradient(135deg, #9f1239 0%, #80427B 40%, #4c1d95 75%, #1e3a8a 100%)">
          <BannerLeft>
            <BannerTitle>BSG Vision {new Date().getFullYear()}</BannerTitle>

            {/* 👇 YAHI add kiya */}
            {isNationalRole && (
              <Box sx={{ marginTop: "6px" }}>
                <ReportButton
                  onClick={() => navigate("/reports/friendtomembergrowth")}
                >
                  Friend to Member Growth
                </ReportButton>
              </Box>
            )}
          </BannerLeft>

          {/* Right side */}
          <BannerPercent>
            <PercentLabel>Achieved</PercentLabel>
            <PercentBig>{percent}%</PercentBig>

            <BannerTrack>
              <BannerFill
                pct={percent}
                fillcolor="rgba(255,255,255,0.85)"
                bardelay="0.3s"
              />
            </BannerTrack>
          </BannerPercent>
        </GradientBanner>

        <CardBody>
          <StatsRow>
            <StatBlock accent="#80427B">
              <StatLabel>Members Added so far</StatLabel>
              <StatValue accent="#80427B">
                {achieved.toLocaleString()}
              </StatValue>
            </StatBlock>

            <StatDivider />

            <StatBlock accent="#1e3a8a">
              <StatLabel>Members to be added</StatLabel>
              <StatValue accent="#1e3a8a">
                {totalGoal.toLocaleString()}
              </StatValue>
            </StatBlock>
          </StatsRow>

          <Footer>
            <FootNote>Remaining members to be added</FootNote>
            <Remaining remcolor="#f59e0b">
              {(totalGoal - achieved).toLocaleString()}
            </Remaining>
          </Footer>
        </CardBody>
      </Root>
      {/* ─── Card 2: Unit / Position Vision ──────────── */}
      {!isNationalRole && (
        <Root>
          <GradientBanner gradient="linear-gradient(135deg, #0f172a 0%, #1d4ed8 45%, #0891b2 80%, #06b6d4 100%)">
            <BannerLeft>
              <BannerTitle>
                {positionRole} Vision {new Date().getFullYear()}
              </BannerTitle>

              <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                <Badge>{UnitName}</Badge>

                <ReportButton
                  onClick={() => navigate("/reports/friendtomembergrowth")}
                >
                  Friend to Member Growth
                </ReportButton>
              </Box>
            </BannerLeft>

            {/* Right side */}
            <BannerPercent>
              <PercentLabel>Achieved</PercentLabel>
              <PercentBig>{delhiPercent}%</PercentBig>
              <BannerTrack>
                <BannerFill
                  pct={delhiPercent}
                  fillcolor="rgba(255,255,255,0.85)"
                  bardelay="0.45s"
                />
              </BannerTrack>
            </BannerPercent>
          </GradientBanner>

          <CardBody>
            <StatsRow>
              <StatBlock accent="#4f46e5">
                <StatLabel>Members added so far</StatLabel>
                <StatValue accent="#4f46e5">
                  {delhiAchieved.toLocaleString()}
                </StatValue>
              </StatBlock>

              <StatDivider />

              <StatBlock accent="#0891b2">
                <StatLabel>Members to be added</StatLabel>
                <StatValue accent="#0891b2">
                  {delhiGoal.toLocaleString()}
                </StatValue>
              </StatBlock>
            </StatsRow>

            <CategoryGrid>
              {UnitCategories.map((cat, i) => (
                <CategoryBox
                  key={cat.label}
                  accent={cat.accent}
                  boxdelay={`${0.62 + i * 0.07}s`}
                >
                  <CatValue accent={cat.accent}>{cat.value}</CatValue>
                  <CatLabel>{cat.label}</CatLabel>
                </CategoryBox>
              ))}
            </CategoryGrid>

            <Footer>
              <FootNote>Remaining members to be added</FootNote>
              <Remaining remcolor="#ef4444">
                {(delhiGoal - delhiAchieved).toLocaleString()}
              </Remaining>
            </Footer>
          </CardBody>
        </Root>
      )}
    </Container>
  );
}

export default BsgVision;
